import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { OrderAmendModal } from '../../components/dem/OrderAmendModal';
import { CallInCreateModal } from '../../components/dem/CallInCreateModal';
import { OrderDetailShell } from '../../components/dem/OrderDetailShell';
import { PoEmailQueueModal } from '../../components/dem/PoEmailQueueModal';
import { PoProcessingModal } from '../../components/dem/PoProcessingModal';
import { UnifiedOrderRow } from '../../components/dem/UnifiedOrderRow';
import { WaProcessingModal } from '../../components/dem/WaProcessingModal';
import { WaQueueModal } from '../../components/dem/WaQueueModal';
import { ActionMenuButton } from '../../components/ui/ActionMenuButton';
import { Modal } from '../../components/ui/Modal';
import { useAuth } from '../../contexts/AuthContext';
import { useDemNotifications } from '../../contexts/DemNotificationsContext';
import { usePoEmailMonitor } from '../../hooks/usePoEmailMonitor';
import { usePoOrdersRealtime } from '../../hooks/usePoOrdersRealtime';
import { useWhatsAppQueueMonitor } from '../../hooks/useWhatsAppQueueMonitor';
import { pushBannerAsNotification } from '../../hooks/useDemPageNotifications';
import { exportPendingCallInOrdersToCloudQueue } from '../../lib/demCallInOrders';
import { formatCloudExportStatusBanner } from '../../lib/demOrderNotifications';
import { useOrderListBlockFlags } from '../../lib/demOrderBlockWarnings';
import { markOrderChecked, orderNeedsCheck, unmarkOrderChecked } from '../../lib/demOrderChecked';
import { dragEventHasFiles, pickPoSourceFile } from '../../lib/demPoExtract';
import { exportPendingPoOrdersToCloudQueue, voidPoOrder } from '../../lib/demPoOrders';
import {
  channelLabel,
  fetchUnifiedOrdersPage,
  filterUnifiedOrdersClient,
  mergeUnifiedOrders,
  UNIFIED_ORDERS_INITIAL_PAGE,
  UNIFIED_ORDERS_MORE_PAGE,
} from '../../lib/demUnifiedOrders';
import {
  getDemActorId,
  getDemActorIds,
  isDemAnonymousActor,
} from '../../lib/demUserNames';
import { resolveAuthEmail } from '../../lib/authEmail';
import { exportPendingWebOrdersToCloudQueue, fetchPendingWebOrders, pendingWebOrdersNeedingDeliveryWaive, waiveWebOrderDeliveryFee } from '../../lib/demWebOrders';
import { exportPendingWhatsAppOrdersToCloudQueue } from '../../lib/demWhatsAppOrders';
import type { WaQueueProcessPayload } from '../../lib/demWhatsAppQueue';
import type { DemOrderChannelFilter, DemPoOrderFilter, DemUnifiedOrder, DemWebOrder } from '../../types/dem';

const CHANNEL_OPTIONS: DemOrderChannelFilter[] = ['All', 'PO', 'B2B', 'WhatsApp', 'Manual'];

export function DemOrdersPage() {
  const { profile, authUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const sessionEmail = resolveAuthEmail(authUser) ?? authUser?.email ?? null;
  const actor = getDemActorId(profile, sessionEmail);
  const actorIds = getDemActorIds(profile, sessionEmail);
  const actorReady = !isDemAnonymousActor(actor);
  const { push: pushNotification } = useDemNotifications();

  const [orders, setOrders] = useState<DemUnifiedOrder[]>([]);
  const [channel, setChannel] = useState<DemOrderChannelFilter>('All');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<DemPoOrderFilter>('All');
  const [cloudSearch, setCloudSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detailOrder, setDetailOrder] = useState<DemUnifiedOrder | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [amendOpen, setAmendOpen] = useState(false);
  const [exportBusy, setExportBusy] = useState(false);
  const [voidBusy, setVoidBusy] = useState(false);
  const [checkBusyId, setCheckBusyId] = useState<string | null>(null);
  const [waiveBusy, setWaiveBusy] = useState(false);
  const [lastSync, setLastSync] = useState('—');
  const [b2bFeePrompt, setB2bFeePrompt] = useState<DemWebOrder[] | null>(null);

  const [newPoOpen, setNewPoOpen] = useState(false);
  const [newPoInitialFile, setNewPoInitialFile] = useState<File | null>(null);
  const [newPoSessionKey, setNewPoSessionKey] = useState(0);
  const [pageDropActive, setPageDropActive] = useState(false);
  const [pageDropError, setPageDropError] = useState<string | null>(null);
  const [emailQueueOpen, setEmailQueueOpen] = useState(false);
  const [emailPulse, setEmailPulse] = useState(false);

  const [waOpen, setWaOpen] = useState(false);
  const [waMode, setWaMode] = useState<'queue' | 'manual'>('manual');
  const [waPayload, setWaPayload] = useState<WaQueueProcessPayload | null>(null);
  const [waSessionKey, setWaSessionKey] = useState(0);
  const [waQueueOpen, setWaQueueOpen] = useState(false);
  const [queuePulse, setQueuePulse] = useState(false);

  const [manualCreateOpen, setManualCreateOpen] = useState(false);

  const listRef = useRef<HTMLDivElement>(null);
  const pageDropDepthRef = useRef(0);
  const lastEmailToastCountRef = useRef(0);
  const lastWaToastCountRef = useRef(0);

  const {
    emails: pendingEmails,
    count: emailQueueCount,
    loading: emailQueueLoading,
    error: emailQueueError,
    lastSync: emailInboxSync,
    newArrivalCount: emailArrivalCount,
    clearNewArrivals: clearEmailArrivals,
    reload: reloadEmailQueue,
  } = usePoEmailMonitor({ enabled: true, pollIntervalMs: 30_000 });

  const {
    messages: queueMessages,
    count: waQueueCount,
    loading: waQueueLoading,
    error: waQueueError,
    lastSync: waQueueLastSync,
    newArrivalCount: waArrivalCount,
    clearNewArrivals: clearWaArrivals,
    reload: reloadWaQueue,
  } = useWhatsAppQueueMonitor({ enabled: true, pollIntervalMs: 15_000 });

  const openPoWithFile = useCallback((file: File | null) => {
    setPageDropError(null);
    setNewPoInitialFile(file);
    setNewPoSessionKey((key) => key + 1);
    setNewPoOpen(true);
  }, []);

  const openWaProcessing = useCallback((mode: 'queue' | 'manual', payload: WaQueueProcessPayload | null = null) => {
    setWaMode(mode);
    setWaPayload(payload);
    setWaSessionKey((key) => key + 1);
    setWaOpen(true);
  }, []);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 900px)');
    const sync = () => {
      if (mq.matches) setStatusFilter('All');
    };
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  useEffect(() => {
    const preventBrowserDrop = (e: DragEvent) => {
      if (!dragEventHasFiles(e.dataTransfer)) return;
      e.preventDefault();
    };
    window.addEventListener('dragover', preventBrowserDrop);
    window.addEventListener('drop', preventBrowserDrop);
    return () => {
      window.removeEventListener('dragover', preventBrowserDrop);
      window.removeEventListener('drop', preventBrowserDrop);
    };
  }, []);

  const handlePageDragEnter = (e: React.DragEvent) => {
    if (!dragEventHasFiles(e.dataTransfer)) return;
    e.preventDefault();
    e.stopPropagation();
    pageDropDepthRef.current += 1;
    setPageDropActive(true);
    setPageDropError(null);
  };

  const handlePageDragLeave = (e: React.DragEvent) => {
    if (!dragEventHasFiles(e.dataTransfer)) return;
    e.preventDefault();
    e.stopPropagation();
    pageDropDepthRef.current = Math.max(0, pageDropDepthRef.current - 1);
    if (pageDropDepthRef.current === 0) setPageDropActive(false);
  };

  const handlePageDragOver = (e: React.DragEvent) => {
    if (!dragEventHasFiles(e.dataTransfer)) return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'copy';
  };

  const handlePageDrop = (e: React.DragEvent) => {
    if (!dragEventHasFiles(e.dataTransfer)) return;
    e.preventDefault();
    e.stopPropagation();
    pageDropDepthRef.current = 0;
    setPageDropActive(false);
    const file = pickPoSourceFile(e.dataTransfer.files);
    if (!file) {
      setPageDropError('Drop a PDF, Excel (.xlsx/.xls/.csv), or email (.eml/.msg) file.');
      return;
    }
    openPoWithFile(file);
  };

  const reload = useCallback(
    async (silent = false, searchKw = cloudSearch, channelFilter = channel) => {
      if (!silent) setLoading(true);
      setError(null);
      try {
        const rows = await fetchUnifiedOrdersPage(
          0,
          UNIFIED_ORDERS_INITIAL_PAGE,
          channelFilter,
          searchKw || undefined,
        );
        setOrders(rows);
        setOffset(rows.length);
        setHasMore(rows.length >= UNIFIED_ORDERS_INITIAL_PAGE);
        setLastSync(
          new Date().toLocaleTimeString('en-GB', {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
          }),
        );
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to load orders.');
        setOrders([]);
        setHasMore(false);
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [channel, cloudSearch],
  );

  useEffect(() => {
    void reload();
  }, [reload]);

  usePoOrdersRealtime({
    onChange: () => {
      void reload(true);
    },
  });

  // Fallback while Realtime publication catches up / brief disconnects.
  useEffect(() => {
    const timer = window.setInterval(() => {
      void reload(true);
    }, 45_000);
    return () => window.clearInterval(timer);
  }, [reload]);

  useEffect(() => {
    if (emailArrivalCount <= 0) return;
    setEmailPulse(true);
    const timer = window.setTimeout(() => setEmailPulse(false), 4500);
    return () => window.clearTimeout(timer);
  }, [emailArrivalCount]);

  useEffect(() => {
    if (emailArrivalCount <= lastEmailToastCountRef.current) return;
    const delta = emailArrivalCount - lastEmailToastCountRef.current;
    lastEmailToastCountRef.current = emailArrivalCount;
    pushNotification({
      tone: 'info',
      title:
        delta === 1
          ? '1 new email order arrived in the inbox queue.'
          : `${delta} new email orders arrived in the inbox queue.`,
      lines: emailQueueCount > 0 ? [`${emailQueueCount} total pending in queue.`] : undefined,
    });
  }, [emailArrivalCount, emailQueueCount, pushNotification]);

  useEffect(() => {
    if (waArrivalCount <= 0) return;
    setQueuePulse(true);
    const timer = window.setTimeout(() => setQueuePulse(false), 4500);
    return () => window.clearTimeout(timer);
  }, [waArrivalCount]);

  useEffect(() => {
    if (waArrivalCount <= lastWaToastCountRef.current) return;
    const delta = waArrivalCount - lastWaToastCountRef.current;
    lastWaToastCountRef.current = waArrivalCount;
    pushNotification({
      tone: 'info',
      title:
        delta === 1
          ? '1 new WhatsApp message arrived in the queue.'
          : `${delta} new WhatsApp messages arrived in the queue.`,
      lines: waQueueCount > 0 ? [`${waQueueCount} total pending in queue.`] : undefined,
    });
  }, [waArrivalCount, waQueueCount, pushNotification]);

  const loadMore = useCallback(async () => {
    if (!hasMore || loadingMore || loading) return;
    setLoadingMore(true);
    setError(null);
    try {
      const rows = await fetchUnifiedOrdersPage(
        offset,
        UNIFIED_ORDERS_MORE_PAGE,
        channel,
        cloudSearch || undefined,
      );
      setOrders((prev) => mergeUnifiedOrders(prev, rows));
      setOffset((prev) => prev + rows.length);
      if (rows.length < UNIFIED_ORDERS_MORE_PAGE) setHasMore(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load more orders.');
    } finally {
      setLoadingMore(false);
    }
  }, [hasMore, loadingMore, loading, offset, cloudSearch, channel]);

  const visibleOrders = useMemo(
    () => filterUnifiedOrdersClient(orders, search, statusFilter),
    [orders, search, statusFilter],
  );
  const blockFlaggedIds = useOrderListBlockFlags(visibleOrders);
  const needsCheckCount = useMemo(() => orders.filter(orderNeedsCheck).length, [orders]);
  const selectedOrder = useMemo(
    () => orders.find((o) => o.order_id === selectedId) ?? null,
    [orders, selectedId],
  );
  const queueBadge = emailQueueCount + waQueueCount;

  const openDetail = (order: DemUnifiedOrder) => {
    setDetailOrder(order);
    setDetailOpen(true);
  };

  // Deep-link from CloudTalk: /dem/orders?order=...
  useEffect(() => {
    const orderId = searchParams.get('order')?.trim();
    if (!orderId) return;
    let cancelled = false;
    void (async () => {
      try {
        const rows = await fetchUnifiedOrdersPage(0, 5, 'All', orderId);
        if (cancelled) return;
        const hit = rows.find((row) => row.order_id === orderId) ?? rows[0] ?? null;
        if (!hit) return;
        setSearch(orderId);
        setCloudSearch(orderId);
        setSelectedId(hit.order_id);
        openDetail(hit);
        const next = new URLSearchParams(searchParams);
        next.delete('order');
        setSearchParams(next, { replace: true });
      } catch {
        /* ignore deep-link miss */
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleExportCombined = async () => {
    if (!actorReady) {
      pushNotification({
        tone: 'warn',
        title: 'Account still loading — wait a moment, then export again.',
        lines: ['Exported by would otherwise be saved as “Web user”.'],
      });
      return;
    }
    const ok = window.confirm(
      'Export your pending PO, WhatsApp, and Manual orders? (Each channel uses its own export path.)',
    );
    if (!ok) return;

    setExportBusy(true);
    setError(null);
    try {
      const po = await exportPendingPoOrdersToCloudQueue(actor, actorIds);
      const wa = await exportPendingWhatsAppOrdersToCloudQueue(actor, actorIds);
      const manual = await exportPendingCallInOrdersToCloudQueue(actor, actorIds);
      const total = po.exported + wa.exported + manual.exported;

      if (total === 0) {
        pushNotification({
          tone: 'neutral',
          title: 'No pending PO / WhatsApp / Manual orders created by you to export.',
        });
      } else {
        if (po.exported > 0) {
          pushBannerAsNotification(
            pushNotification,
            formatCloudExportStatusBanner(po.exported, po.notifications, 'PO order'),
          );
        }
        if (wa.exported > 0) {
          pushBannerAsNotification(
            pushNotification,
            formatCloudExportStatusBanner(wa.exported, wa.notifications, 'WhatsApp order'),
          );
        }
        if (manual.exported > 0) {
          pushBannerAsNotification(
            pushNotification,
            formatCloudExportStatusBanner(manual.exported, manual.notifications, 'Manual order'),
          );
        }
      }
      await reload(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Cloud export failed.');
    } finally {
      setExportBusy(false);
    }
  };

  const runB2bExport = useCallback(async () => {
    const { exported, notifications } = await exportPendingWebOrdersToCloudQueue(actor);
    if (exported === 0) {
      pushNotification({ tone: 'neutral', title: 'No pending B2B orders to export.' });
    } else {
      pushBannerAsNotification(
        pushNotification,
        formatCloudExportStatusBanner(exported, notifications, 'B2B order'),
      );
    }
    await reload(true);
  }, [actor, pushNotification, reload]);

  const handleExportB2B = async () => {
    if (!actorReady) {
      pushNotification({
        tone: 'warn',
        title: 'Account still loading — wait a moment, then export again.',
        lines: ['Exported by would otherwise be saved as “Web user”.'],
      });
      return;
    }
    setExportBusy(true);
    setError(null);
    try {
      const pending = await fetchPendingWebOrders();
      if (pending.length === 0) {
        pushNotification({ tone: 'neutral', title: 'No pending B2B orders to export.' });
        return;
      }

      const needWaive = pendingWebOrdersNeedingDeliveryWaive(pending);
      if (needWaive.length > 0) {
        setB2bFeePrompt(needWaive);
        return;
      }

      const ok = window.confirm(
        `Push ${pending.length} pending B2B (web) order(s) to the cloud export queue?`,
      );
      if (!ok) return;

      await runB2bExport();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Cloud export failed.');
    } finally {
      setExportBusy(false);
    }
  };

  const handleB2bFeePrompt = async (choice: 'waive' | 'continue' | 'cancel') => {
    const feeOrders = b2bFeePrompt ?? [];
    setB2bFeePrompt(null);
    if (choice === 'cancel') {
      pushNotification({ tone: 'neutral', title: 'B2B export cancelled.' });
      return;
    }

    setExportBusy(true);
    setError(null);
    try {
      if (choice === 'waive') {
        for (const order of feeOrders) {
          await waiveWebOrderDeliveryFee(order.order_id);
        }
        pushNotification({
          tone: 'ok',
          title: `Waived delivery fee on ${feeOrders.length} B2B order(s).`,
        });
      }
      await runB2bExport();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Cloud export failed.');
    } finally {
      setExportBusy(false);
    }
  };

  const handleVoid = async () => {
    if (!selectedOrder) return;
    const ok = window.confirm(`Void order ${selectedOrder.order_id}?`);
    if (!ok) return;

    setVoidBusy(true);
    setError(null);
    try {
      await voidPoOrder(selectedOrder.order_id, actor);
      pushNotification({ tone: 'ok', title: `Order ${selectedOrder.order_id} voided.` });
      setSelectedId(null);
      await reload(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to void order.');
    } finally {
      setVoidBusy(false);
    }
  };

  const handleMarkChecked = async (order: DemUnifiedOrder) => {
    setCheckBusyId(order.order_id);
    setError(null);
    try {
      await markOrderChecked(order.order_id, actor);
      pushNotification({ tone: 'ok', title: `Order ${order.order_id} marked Checked.` });
      await reload(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to mark Checked.');
    } finally {
      setCheckBusyId(null);
    }
  };

  const handleUnmarkChecked = async (order: DemUnifiedOrder) => {
    setCheckBusyId(order.order_id);
    setError(null);
    try {
      await unmarkOrderChecked(order.order_id);
      pushNotification({ tone: 'neutral', title: `Checked cleared for ${order.order_id}.` });
      await reload(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to undo Checked.');
    } finally {
      setCheckBusyId(null);
    }
  };

  const handleWaiveFee = async () => {
    if (!detailOrder || detailOrder.source !== 'Web') return;
    setWaiveBusy(true);
    setError(null);
    try {
      await waiveWebOrderDeliveryFee(detailOrder.order_id);
      pushNotification({ tone: 'ok', title: `Delivery fee waived for ${detailOrder.order_id}.` });
      await reload(true);
      const refreshed = await fetchUnifiedOrdersPage(0, 1, 'B2B', detailOrder.order_id);
      const next = refreshed.find((row) => row.order_id === detailOrder.order_id) ?? null;
      if (next) setDetailOrder(next);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to waive delivery fee.');
    } finally {
      setWaiveBusy(false);
    }
  };

  // Deep cloud search (incl. customer name/no via customers table) while typing.
  useEffect(() => {
    const kw = search.trim();
    const timer = window.setTimeout(() => {
      setCloudSearch((prev) => (prev === kw ? prev : kw));
    }, 350);
    return () => window.clearTimeout(timer);
  }, [search]);

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return;
    setCloudSearch(search.trim());
  };

  const onListScroll = () => {
    const el = listRef.current;
    if (!el) return;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 80) {
      void loadMore();
    }
  };

  const refreshDetailOrder = async (orderId: string) => {
    await reload(true);
    const channel =
      detailOrder?.source === 'Desktop'
        ? 'PO'
        : detailOrder?.source === 'Web'
          ? 'B2B'
          : detailOrder?.source === 'WhatsApp'
            ? 'WhatsApp'
            : detailOrder?.source === 'CallIn'
              ? 'Manual'
              : 'All';
    const refreshed = await fetchUnifiedOrdersPage(0, 1, channel, orderId);
    const next = refreshed.find((row) => row.order_id === orderId) ?? null;
    if (next) setDetailOrder(next);
  };

  return (
    <div
      className={`dem-po-orders-page dem-unified-orders-page dem-po-orders-page--floating dem-list-scroll${pageDropActive ? ' dem-po-orders-page--drop-active' : ''}`}
      onDragEnter={handlePageDragEnter}
      onDragLeave={handlePageDragLeave}
      onDragOver={handlePageDragOver}
      onDrop={handlePageDrop}
    >
      {pageDropActive ? (
        <div className="dem-po-orders-drop-overlay" aria-hidden>
          <p className="dem-po-orders-drop-overlay__title">Drop to create New PO</p>
          <p className="dem-po-orders-drop-overlay__hint">PDF, Excel, CSV, or email (.eml / .msg)</p>
        </div>
      ) : null}

      <div className="dem-floating-page-actions">
        <p className="dem-po-orders-status dem-po-orders-status--inline">
          Last sync: {lastSync} · Loaded: {orders.length}
        </p>
        <div className="dem-floating-page-actions__btns dem-po-orders-header-actions">
          <button
            type="button"
            className="btn-secondary btn-sm"
            disabled={exportBusy || loading}
            onClick={() => void reload()}
          >
            Refresh
          </button>
          <ActionMenuButton
            label="Queue"
            variant="secondary"
            disabled={loading}
            badge={queueBadge > 0 ? queueBadge : undefined}
            className={emailPulse || queuePulse ? 'dem-po-orders-email-queue-btn--pulse' : undefined}
            items={[
              {
                id: 'email-queue',
                label: emailQueueCount > 0 ? `Email queue (${emailQueueCount})` : 'Email queue',
                onSelect: () => {
                  clearEmailArrivals();
                  setEmailPulse(false);
                  setEmailQueueOpen(true);
                },
              },
              {
                id: 'wa-queue',
                label: waQueueCount > 0 ? `WA queue (${waQueueCount})` : 'WA queue',
                onSelect: () => {
                  clearWaArrivals();
                  setQueuePulse(false);
                  setWaQueueOpen(true);
                },
              },
            ]}
          />
          <ActionMenuButton
            label="New"
            variant="primary"
            disabled={loading || !actorReady}
            items={[
              { id: 'new-po', label: 'PO', onSelect: () => openPoWithFile(null) },
              { id: 'new-wa', label: 'WhatsApp', onSelect: () => openWaProcessing('manual') },
              { id: 'new-manual', label: 'Manual', onSelect: () => setManualCreateOpen(true) },
            ]}
          />
          <ActionMenuButton
            label={exportBusy ? 'Exporting…' : 'Export'}
            variant="secondary"
            disabled={exportBusy || loading || !actorReady}
            items={[
              {
                id: 'export-combined',
                label: 'PO + WhatsApp + Manual',
                onSelect: () => void handleExportCombined(),
              },
              {
                id: 'export-b2b',
                label: 'B2B only',
                onSelect: () => void handleExportB2B(),
              },
            ]}
          />
          <button
            type="button"
            className="btn-secondary btn-sm dem-po-orders-void-btn"
            disabled={!selectedOrder || voidBusy || loading}
            onClick={() => void handleVoid()}
          >
            {voidBusy ? 'Voiding…' : 'Void selected'}
          </button>
        </div>
      </div>

      <div className="dem-po-orders-toolbar">
        {pageDropError ? <p className="alert alert-error">{pageDropError}</p> : null}
        <div className="dem-po-orders-toolbar__row dem-unified-orders-toolbar__row">
          <label className="dem-unified-orders-channel">
            <span className="sr-only">Order type</span>
            <select
              className="field-input dem-unified-orders-channel__select"
              value={channel}
              onChange={(e) => {
                const next = e.target.value as DemOrderChannelFilter;
                setChannel(next);
                setSelectedId(null);
                setCloudSearch(search.trim());
              }}
              aria-label="Filter by order type"
            >
              {CHANNEL_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option === 'All' ? 'All (default)' : channelLabel(option)}
                </option>
              ))}
            </select>
          </label>
          <div className="dem-orders-filters" role="group" aria-label="Filter by status">
            {(
              [
                ['All', 'All'],
                ['Pending', 'Pending'],
                ['TodayProcessed', 'Today processed'],
                ['NeedsCheck', `Needs check${needsCheckCount > 0 ? ` (${needsCheckCount})` : ''}`],
              ] as const
            ).map(([id, label]) => (
              <button
                key={id}
                type="button"
                className={`dem-filter-btn${statusFilter === id ? ' dem-filter-btn--active' : ''}`}
                onClick={() => setStatusFilter(id)}
              >
                {label}
              </button>
            ))}
          </div>
          <label className="dem-po-orders-search">
            <span className="sr-only">Search orders</span>
            <input
              type="search"
              className="dem-po-orders-search__input"
              placeholder="Order ID, customer, exported by…"
              title="Searches order ID, PO/ref, customer name/no., exported by, created by, and remarks"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={handleSearchKeyDown}
            />
          </label>
        </div>
      </div>

      {error ? <p className="alert alert-error">{error}</p> : null}
      {emailQueueError ? <p className="alert alert-error">Email inbox: {emailQueueError}</p> : null}
      {waQueueError ? <p className="alert alert-error">WA inbox: {waQueueError}</p> : null}

      <div className="dem-po-orders-table-wrap">
        <div
          className="dem-po-orders-table-scroll"
          ref={listRef}
          onScroll={onListScroll}
        >
          <div className="dem-po-orders-table__head" aria-hidden>
            <span>Order ID</span>
            <span>Customer name / no.</span>
            <span>Created at</span>
            <span>Delivery date</span>
            <span>PO no.</span>
            <span>Remarks</span>
            <span className="dem-po-orders-table__head-amount">Amount</span>
            <span>Status</span>
            <span>Created / Exported by</span>
          </div>

          {loading && orders.length === 0 ? (
            <p className="dem-po-orders-empty">Loading orders…</p>
          ) : visibleOrders.length === 0 ? (
            <p className="dem-po-orders-empty">No orders found.</p>
          ) : (
            visibleOrders.map((order) => (
              <UnifiedOrderRow
                key={order.order_id}
                order={order}
                selected={selectedId === order.order_id}
                checkBusy={checkBusyId === order.order_id}
                blockWarning={blockFlaggedIds.has(order.order_id)}
                onSelect={(row) => setSelectedId(row.order_id)}
                onOpen={openDetail}
                onMarkChecked={(row) => void handleMarkChecked(row)}
                onUnmarkChecked={(row) => void handleUnmarkChecked(row)}
              />
            ))
          )}

          {loadingMore ? <p className="dem-po-orders-empty">Loading more…</p> : null}
        </div>
      </div>

      <OrderDetailShell
        order={detailOrder}
        open={detailOpen && Boolean(detailOrder)}
        onClose={() => {
          setDetailOpen(false);
          setDetailOrder(null);
        }}
        actor={actor}
        actorIds={actorIds}
        onSaved={() => {
          if (!detailOrder) return;
          pushNotification({ tone: 'ok', title: `Order ${detailOrder.order_id} updated.` });
          void refreshDetailOrder(detailOrder.order_id);
        }}
        onAmend={() => setAmendOpen(true)}
        checkBusy={detailOrder ? checkBusyId === detailOrder.order_id : false}
        onMarkChecked={detailOrder ? () => void handleMarkChecked(detailOrder) : undefined}
        onUnmarkChecked={detailOrder ? () => void handleUnmarkChecked(detailOrder) : undefined}
        onWaiveFee={
          detailOrder?.source === 'Web' && detailOrder.status === 'Pending'
            ? () => void handleWaiveFee()
            : undefined
        }
        waiveBusy={waiveBusy}
      />

      <Modal
        open={b2bFeePrompt != null}
        onClose={() => void handleB2bFeePrompt('cancel')}
        title="Delivery fee before B2B export"
        nested
      >
        <div className="dem-b2b-fee-prompt">
          <p className="dem-b2b-fee-prompt__lead">
            {b2bFeePrompt?.length ?? 0} pending B2B order(s) still have a delivery fee (below MOA).
            Choose how to continue:
          </p>
          <ul className="dem-b2b-fee-prompt__list">
            {(b2bFeePrompt ?? []).map((order) => (
              <li key={order.order_id}>
                <strong>{order.order_id}</strong>
                <span>
                  {' '}
                  · {order.customer_name}
                  {order.customer_code ? ` (${order.customer_code})` : ''}
                </span>
              </li>
            ))}
          </ul>
          <div className="dem-b2b-fee-prompt__actions">
            <button
              type="button"
              className="btn-secondary"
              disabled={exportBusy}
              onClick={() => void handleB2bFeePrompt('cancel')}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-secondary"
              disabled={exportBusy}
              onClick={() => void handleB2bFeePrompt('continue')}
            >
              Continue
            </button>
            <button
              type="button"
              className="btn-primary"
              disabled={exportBusy}
              onClick={() => void handleB2bFeePrompt('waive')}
            >
              Waive
            </button>
          </div>
        </div>
      </Modal>

      <OrderAmendModal
        open={amendOpen && Boolean(detailOrder)}
        orderId={detailOrder?.order_id ?? ''}
        actor={actor}
        onClose={() => setAmendOpen(false)}
        onSaved={() => {
          pushNotification({
            tone: 'ok',
            title: `Add-on saved on ${detailOrder?.order_id ?? 'order'}.`,
          });
          if (detailOrder) void refreshDetailOrder(detailOrder.order_id);
          else void reload(true);
        }}
      />

      <PoProcessingModal
        key={newPoSessionKey}
        open={newPoOpen}
        initialFile={newPoInitialFile}
        onClose={() => {
          setNewPoOpen(false);
          setNewPoInitialFile(null);
        }}
        actor={actor}
        onSaved={(lines, tone) => {
          pushNotification({ tone, title: lines[0] ?? 'PO order saved.', lines: lines.slice(1) });
          void reload(true);
        }}
      />

      <PoEmailQueueModal
        open={emailQueueOpen}
        onClose={() => setEmailQueueOpen(false)}
        emails={pendingEmails}
        loading={emailQueueLoading}
        error={emailQueueError}
        lastSync={emailInboxSync}
        onReload={reloadEmailQueue}
        onDismissed={clearEmailArrivals}
        onProcessFile={(file) => {
          clearEmailArrivals();
          setEmailQueueOpen(false);
          openPoWithFile(file);
        }}
      />

      <WaQueueModal
        open={waQueueOpen}
        onClose={() => setWaQueueOpen(false)}
        messages={queueMessages}
        loading={waQueueLoading}
        error={waQueueError}
        lastSync={waQueueLastSync}
        onReload={reloadWaQueue}
        onDismissed={clearWaArrivals}
        onProcess={(payload) => {
          clearWaArrivals();
          setWaQueueOpen(false);
          openWaProcessing('queue', payload);
        }}
      />

      <WaProcessingModal
        key={waSessionKey}
        open={waOpen}
        mode={waMode}
        initialPayload={waPayload}
        onClose={() => {
          setWaOpen(false);
          setWaPayload(null);
        }}
        actor={actor}
        onSaved={(lines, tone) => {
          pushNotification({
            tone,
            title: lines[0] ?? 'WhatsApp order saved.',
            lines: lines.slice(1),
          });
          void reload(true);
          void reloadWaQueue(true);
        }}
      />

      <CallInCreateModal
        open={manualCreateOpen}
        onClose={() => setManualCreateOpen(false)}
        actor={actor}
        onSaved={(orderId) => {
          pushNotification({
            tone: 'ok',
            title: `Manual order ${orderId} saved.`,
          });
          void reload(true);
        }}
      />
    </div>
  );
}
